import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaMapMarkerAlt, FaPaperPlane, FaGlobeAmericas, 
  FaChevronRight, FaCheckCircle, FaStar, FaChevronDown, 
  FaTimesCircle, FaClock, FaBus, FaHotel, FaUtensils, FaShieldAlt,
  FaMountain, FaSnowflake, FaCloudSun
} from 'react-icons/fa';
import './LehLadakhHimalayan.css';

const LehLadakhHimalayan = () => {
  const [openSection, setOpenSection] = useState(null);

  const itineraryRef = useRef(null);
  const highlightsRef = useRef(null);
  const inclusionsRef = useRef(null);
  const aboutRef = useRef(null);

  const scrollToSection = (ref) => {
    window.scrollTo({ top: ref.current.offsetTop - 100, behavior: 'smooth' });
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } }
  };

  const itineraryData = [
    {
      day: 1,
      title: "Arrival in Leh (Acclimatization Day)",
      points: [
        "Arrival at Kushok Bakula Rimpochee Airport and transfer to hotel.",
        "Leh is at 3,500m altitude; proper rest is mandatory for acclimatization.",
        "Avoid heavy physical activity to allow the body to adjust.",
        "If comfortable, enjoy a light evening walk in Leh Market.",
        "Dinner and overnight stay in Leh."
      ],
      icon: <FaCloudSun />
    },
    {
      day: 2,
      title: "Leh Local Sightseeing",
      points: [
        "Visit Shanti Stupa for panoramic views of Leh town.",
        "Explore the historic Leh Palace overlooking the city.",
        "Visit Thiksey Monastery, one of the largest in Ladakh.",
        "Explore Shey Palace and the local Tibetan markets.",
        "Dinner and overnight stay in Leh."
      ],
      icon: <FaMountain />
    },
    {
      day: 3,
      title: "Leh to Nubra Valley via Khardung La",
      points: [
        "Drive to Nubra Valley (120 km) via the legendary Khardung La pass (5,359m).",
        "Cross one of the highest motorable roads in the world.",
        "Visit Diskit Monastery and the giant Maitreya Buddha statue.",
        "Explore Hunder Sand Dunes with optional Bactrian camel rides.",
        "Dinner and overnight stay in Nubra Valley."
      ],
      icon: <FaBus />
    },
    {
      day: 4,
      title: "Nubra Valley to Pangong Lake",
      points: [
        "Drive to the stunning Pangong Lake (160 km) at 4,350m altitude.",
        "Witness the lake's famous changing shades of blue.",
        "Spend time exploring the lakeside and peaceful Himalayan surroundings.",
        "Enjoy the breathtaking scenery spanning across India and China.",
        "Dinner and overnight stay near Pangong Lake."
      ],
      icon: <FaSnowflake />
    },
    {
      day: 5,
      title: "Pangong Lake to Leh",
      points: [
        "Drive back to Leh (160 km) through dramatic mountain landscapes.",
        "Cross Chang La pass on the way back.",
        "Check in to the hotel and enjoy free time for relaxation.",
        "Evening free for final souvenir shopping in the local market.",
        "Dinner and overnight stay in Leh."
      ],
      icon: <FaMapMarkerAlt />
    },
    {
      day: 6,
      title: "Leh Monasteries & Local Exploration",
      points: [
        "Visit Hemis Monastery, one of the richest in the region.",
        "Explore the Hall of Fame Museum dedicated to Indian Army soldiers.",
        "Visit Magnetic Hill (optical illusion point) and Indus-Zanskar Sangam.",
        "Explore local cafés and the vibrant culture of Leh.",
        "Dinner and overnight stay in Leh."
      ],
      icon: <FaGlobeAmericas />
    },
    {
      day: 7,
      title: "Departure from Leh",
      points: [
        "Breakfast and hotel check-out.",
        "Transfer to Leh Airport for your onward journey.",
        "Tour concludes with unforgettable memories of the majestic Ladakh."
      ],
      icon: <FaCheckCircle />
    }
  ];

  return (
    <div className="trip-container">
      {/* 1. HEADER */}
      <motion.header initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="trip-header">
        <div className="header-left">
          <h1 className="trip-title">Leh Ladakh Himalayan Adventure – <br /> Leh, Nubra Valley & Pangong Lake</h1>
          <p className="trip-duration">Duration: 6 Nights & 7 Days of High-Altitude Thrill</p>
        </div>
        <div className="header-right">
          <div className="info-row"><FaMapMarkerAlt className="header-icon" /> <div className="info-text"><span>Destination:</span><p>Leh, Nubra, Pangong</p></div></div>
          <div className="info-row"><FaPaperPlane className="header-icon" /> <div className="info-text"><span>Service Type:</span><p>Adventure & Scenic Tour</p></div></div>
          <div className="info-row type-tag"><FaMountain className="header-icon" /><p>Roof of the World</p></div>
        </div>
      </motion.header>

      {/* 2. IMAGE GRID */}
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="image-grid-container">
        <div className="grid-item large-vertical">
          <img src="https://images.unsplash.com/photo-1581791534721-e599df4417f7?q=80&w=1200" alt="Pangong Lake" />
        </div>
        <div className="grid-item middle-box">
          <img src="https://plus.unsplash.com/premium_photo-1661962944134-45f9a031cd5e?q=80&w=387&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Nubra Valley" />
        </div>
        <div className="right-stack">
          <div className="grid-item small-h">
            <img src="https://images.unsplash.com/photo-1744197068961-18194526c236?q=80&w=941&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Leh Monastery" />
          </div>
          <div className="grid-item small-h">
            <img src="https://images.unsplash.com/photo-1632731936453-0b646fb1efb2?q=80&w=870&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Khardung La" />
          </div>
        </div>
      </motion.div>

      {/* 3. STICKY NAV */}
      <nav className="sticky-nav">
        <button className="tab-link" onClick={() => scrollToSection(itineraryRef)}>Itinerary</button>
        <button className="tab-link" onClick={() => scrollToSection(highlightsRef)}>Highlights</button>
        <button className="tab-link" onClick={() => scrollToSection(inclusionsRef)}>Policy</button>
        <button className="tab-link" onClick={() => scrollToSection(aboutRef)}>About Trip</button>
      </nav>

      <div className="content-section-wrapper">
        {/* ITINERARY SECTION */}
        <section ref={itineraryRef} className="scroll-section">
          <h2 className="section-main-title">Detailed Itinerary</h2>
          <div className="days-list">
            {itineraryData.map((item) => (
              <motion.div key={item.day} initial="hidden" whileInView="visible" viewport={{ once: false, amount: 0.2 }} variants={fadeInUp} className="day-block">
                <div className="day-header">
                  <span className="day-badge">Day {item.day}</span>
                  <h3>{item.title}</h3>
                </div>
                <div className="day-body">
                  {item.points.map((p, i) => (
                    <div key={i} className="point-item">
                      <FaChevronRight className="bullet-icon" />
                      <p>{p}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* HIGHLIGHTS SECTION */}
        <motion.section ref={highlightsRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <h2 className="section-main-title">Trip Highlights</h2>
          <div className="highlights-grid">
            <div className="h-item"><FaStar className="star-icon" /> <p>Legendary Khardung La - Highest motorable road</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Magical Pangong Lake changing colors</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Hunder Sand Dunes & Bactrian Camel Rides</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Ancient Thiksey and Hemis Monasteries</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Panoramic views from Shanti Stupa</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Magnetic Hill & Indus-Zanskar Sangam</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Full acclimatization day in Leh town</p></div>
          </div>
        </motion.section>

        {/* INCLUSIONS & EXCLUSIONS */}
        <motion.section ref={inclusionsRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <div className="policy-box inclusion-box">
            <h4 className="p-title green"><FaCheckCircle /> What's Included</h4>
            <ul className="p-list">
              <li><FaHotel /> Accommodation in hotels / premium camps as per itinerary</li>
              <li><FaUtensils /> Meal Plan: Daily Breakfast and Dinner</li>
              <li><FaBus /> Private vehicle for all transfers and sightseeing</li>
              <li><FaShieldAlt /> Inner Line Permits for Nubra Valley and Pangong Lake</li>
              <li><FaCheckCircle /> Driver allowance, fuel, toll taxes, and parking</li>
              <li><FaShieldAlt /> Tour coordination and assistance by Chandrama Holidays</li>
            </ul>
          </div>
          <div className="policy-box exclusion-box">
            <h4 className="p-title red"><FaTimesCircle /> What's Excluded</h4>
            <ul className="p-list">
              <li><FaTimesCircle /> Airfare or train tickets</li>
              <li><FaTimesCircle /> Camel ride charges at Hunder Sand Dunes</li>
              <li><FaTimesCircle /> Entry tickets to monasteries or museums</li>
              <li><FaTimesCircle /> Oxygen cylinders or emergency medical rescue</li>
              <li><FaTimesCircle /> Personal expenses (Tips, Laundry, Shopping)</li>
              <li><FaTimesCircle /> Additional costs due to landslides or weather closures</li>
            </ul>
          </div>
        </motion.section>

        {/* ABOUT SECTION */}
        <motion.section ref={aboutRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <h2 className="section-main-title">Tour Overview</h2>
          <p className="about-text">
            Experience the breathtaking landscapes of Ladakh, a land of dramatic mountain passes, high-altitude lakes, and ancient monasteries. This journey takes travelers through some of the most spectacular regions including Leh, Nubra Valley, and the stunning Pangong Lake.
            <br /><br />
            From crossing some of the world’s highest motorable roads to witnessing the magical colors of Pangong Lake, this tour offers an unforgettable adventure. The itinerary is designed with proper acclimatization for Ladakh’s high altitude while allowing travelers to explore the region’s spiritual heritage.
          </p>
        </motion.section>

        {/* FOOTER LINKS */}
        <div className="footer-links-container" style={{marginTop: '40px'}}>
          <div className="accordion-item">
            <Link to="/terms-conditions" className="accordion-header" style={{ textDecoration: 'none', color: 'inherit' }}>
              <span>Terms & Conditions</span>
              <FaChevronDown className="arrow-icon" style={{ transform: 'rotate(-90deg)' }} /> 
            </Link>
          </div>
          <div className="accordion-item">
            <Link to="/privacy-policy" className="accordion-header" style={{ textDecoration: 'none', color: 'inherit' }}>
              <span>Privacy Policy</span>
              <FaChevronDown className="arrow-icon" style={{ transform: 'rotate(-90deg)' }} />
            </Link>
          </div>
        </div>

        {/* IMPORTANT NOTE */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section travel-info-grid">
          <div className="info-card note-card" style={{ width: '100%' }}>
            <h4 className="info-card-title"><FaPaperPlane /> Important Note</h4>
            <p>Flight and train tickets can be arranged by <strong>Chandrama Holidays</strong> on request. Please note that oxygen cylinders and emergency rescue costs are not included and must be handled by travelers if required due to altitude sickness.</p>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default LehLadakhHimalayan;